/* eslint-disable @typescript-eslint/no-unused-vars */
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects'

import { createSagas } from 'src/helpers/redux'
import * as storageObjectsHelpers from 'src/helpers/storageObjects'
import store from 'src/store'
import { IResponse } from 'src/services/storage/project/http'
import * as schemaService from 'src/services/storage/project/scheme'
import * as smartListService from 'src/services/storage/project/scheme/smart-list'
import * as stageSortService from 'src/services/storage/project/scheme/objects/stage-sort'
import { realtimeService } from 'src/services/realtime'

import * as appStore from 'src/store/modules/app'
import { IStateData as AppStoreState } from 'src/store/modules/app/state'
import { IModuleConfigStrict, Setters } from './index'
import * as state from './state'
import { Actions, createFullfiledAction } from './actions'
import { Selectors } from './selectors'
import { camelCase } from 'src/utils/camelCase'
import {
  getRelatedFieldsParam,
  isRelatedColumn,
} from 'src/helpers/tableColumns'

import * as app from 'src/store/modules/app'
import {
  IStorageFilter,
  stripFilterMetadata,
} from 'src/services/storage/project/scheme/objects/filters/types'

// The UI metadata (`additionalParams`) is persisted with smart lists / exports but never sent to list endpoints
const withoutFilterMetadata = <T extends IStorageFilter | null | undefined>(
  filters: T
): T => (filters ? (stripFilterMetadata(filters) as T) : filters)

export default (
  config: IModuleConfigStrict,
  actions: Actions,
  setters: Setters,
  selectors: Selectors
) => {
  // Helper function to subscribe to smart list recalculation events
  // Uses consistent key format to prevent duplicate subscriptions
  const subscribeToSmartListRecalculation = () => {
    const key = `smartList`

    realtimeService.only<smartListService.IGetSmartListItemData>(
      {
        key,
        event: 'recalculated',
        channel: 'storage:smart_lists',
      },
      (data) => {
        store.dispatch(setters.updateSmartListItem(data))
        store.dispatch(actions.syncTableColumns.pending())
      }
    )
  }

  type IColumnStoreSlice = {
    slice: {
      actions: {
        setListAddFirstBoard: (data: schemaService.IGetObjectsData) => {
          type: string
          payload: schemaService.IGetObjectsData
        }
        setListItem: (data: schemaService.IGetObjectsData) => {
          type: string
          payload: schemaService.IGetObjectsData
        }
        deleteObjectItem: (uniqIds: string[]) => {
          type: string
          payload: string[]
        }
      }
    }
  }

  const loadColumnStore = function* (columnPrefix: string) {
    const { getStore } = (yield import('./index')) as {
      getStore: (config: {
        prefix: string
        schemeName: string
      }) => IColumnStoreSlice
    }

    return getStore({
      prefix: columnPrefix,
      schemeName: config.schemeName,
    })
  }

  const resolveBoardColumn = function* (
    boardIdentifier: string,
    object: schemaService.IGetObjectsData
  ) {
    const boardList: ReturnType<Selectors['boardList']> = yield select(
      selectors.boardList
    )
    const board = boardList?.find((item) => item.identifier === boardIdentifier)
    const stages = board?.stages
      ? [...board.stages].sort((a, b) => a.index - b.index)
      : []
    const stageValue = object.values?.[camelCase(boardIdentifier)]
    // an unknown stage value must not land in a hidden final column
    const stage =
      stages.find((item) => item.choice.value === stageValue) ||
      stages.find((item) =>
        storageObjectsHelpers.isStageVisible(board, item)
      ) ||
      stages.find((item) => item.enable) ||
      stages[0]

    if (!stage || !config._prefix) {
      return undefined
    }

    const columnPrefix = `${config._prefix}/${stage.choice.id}`
    const columnStore: IColumnStoreSlice = yield call(
      loadColumnStore,
      columnPrefix
    )

    return {
      stage,
      columnPrefix,
      columnStore,
      // the column of a hidden final stage lists no cards: nothing to insert there
      isHidden: storageObjectsHelpers.isStageHiddenFinal(board, stage),
    }
  }

  return createSagas<Actions>(actions, {
    getMoreObjectList: function* ({ payload }) {
      const receivedObjects: IResponse<schemaService.IGetObjectsData[]> =
        yield call(schemaService.getObjects, {
          schemeName: config.schemeName,
          ...payload,
          filters: withoutFilterMetadata(payload.filters),
        })

      yield put(
        setters.mergeData({
          items: receivedObjects.data,
          previousCursor: receivedObjects.meta.prevCursor,
          nextCursor: receivedObjects.meta.nextCursor,
        })
      )
    },
    changeObjectList: function* ({ payload }) {
      yield put(setters.setChangeObjectList(payload))
    },
    deleteObjectItem: {
      pending: function* ({ payload }) {
        const response: IResponse<
          schemaService.IDeleteValidationObjectByIdData[]
        > = yield call(schemaService.deleteObject, payload)

        if (
          payload.action === schemaService.EDeleteObjectAction.VALIDATE_DELETE
        ) {
          const validationResult = Object.values(response.data)

          yield put(
            setters.setObjectDeleteValidation(
              validationResult.length > 0
                ? validationResult
                : [{ type: 'confirmation' }]
            )
          )
        }
      },

      clear: function* () {
        yield put(setters.setObjectDeleteValidation(null))
      },
    },
    updateObjectItem: function* ({ payload }) {
      yield put(setters.updateItem(payload))
    },

    postDeleteAllSchemaObject: {
      pending: function* ({ payload }) {
        yield call(schemaService.postDeleteAllSchemaObjects, {
          ...payload,
          filters: withoutFilterMetadata(payload.filters),
        })

        yield put(
          setters.setList({
            parentObjectUniqId: undefined,
            items: [],
            previousCursor: null,
            nextCursor: null,
            total: undefined,
          })
        )
      },
      clear: function* () {
        yield put(setters.setObjectDeleteValidation(null))
      },
    },

    postDeleteAllSchemaObjectsValidation: function* ({ payload }) {
      const response: IResponse<
        schemaService.IDeleteValidationObjectByIdData[]
      > = yield call(schemaService.postDeleteAllSchemaObjectsValidation, {
        ...payload,
        filters: withoutFilterMetadata(payload.filters),
      })

      yield put(setters.setObjectDeleteValidation(response.data))
    },

    getObjectsList: {
      effect: 'latest',
      pending: function* ({ payload }) {
        if (payload.search) {
          yield delay(2000)
        }

        yield put(actions.watcherListEvents(payload))

        const objectList: IResponse<schemaService.IGetObjectsData[]> =
          yield call(schemaService.getObjects, {
            schemeName: config.schemeName,
            ...payload,
            filters: withoutFilterMetadata(payload.filters),
          })

        yield put(
          setters.setList({
            parentObjectUniqId: payload.parentObjectUniqId || undefined,
            items: objectList.data,
            previousCursor: objectList.meta.prevCursor,
            nextCursor: objectList.meta.nextCursor,
            total: objectList.meta.total,
          })
        )
      },
      clear: function* () {
        yield put(
          setters.setList({
            items: [],
            parentObjectUniqId: undefined,
            previousCursor: null,
            nextCursor: null,
          })
        )
      },
      rejected: function* () {
        yield put(
          setters.setList({
            items: [],
            parentObjectUniqId: undefined,
            previousCursor: null,
            nextCursor: null,
          })
        )
      },
    },
    // Mock until /objects/by-communication-type returns the objects list shape.
    getObjectsByCommunicationType: {
      effect: 'latest',
      pending: function* () {
        yield put(
          setters.setList({
            items: [],
            parentObjectUniqId: undefined,
            previousCursor: null,
            nextCursor: null,
            total: 0,
          })
        )
      },
      clear: function* () {
        yield put(
          setters.setList({
            items: [],
            parentObjectUniqId: undefined,
            previousCursor: null,
            nextCursor: null,
          })
        )
      },
      rejected: function* () {
        yield put(
          setters.setList({
            items: [],
            parentObjectUniqId: undefined,
            previousCursor: null,
            nextCursor: null,
          })
        )
      },
    },
    watcherListEvents: function* ({ payload }) {
      const listKey = `${
        config.schemeName
      }${actions.watcherListEvents.pending.toString()}`

      const channel = `storage:${config.schemeName}`

      const insertEvent = {
        key: `${listKey}/insert`,
        event: 'insert',
        channel,
      }
      const updateEvent = {
        key: `${listKey}/update`,
        event: 'update',
        channel,
      }
      const deleteEvent = {
        key: `${listKey}/delete`,
        event: 'delete',
        channel,
      }

      if (
        !payload.filters &&
        !payload.search &&
        !payload.smartListId &&
        !payload.sort
      ) {
        realtimeService.only<schemaService.IGetObjectsData>(
          insertEvent,
          (data) => store.dispatch(actions.getObjectAbly(data))
        )

        // Realtime `update` payloads carry no `relatedValues`: setListItem shallow-merges
        // them, so related cells keep the values of the last list request until it reloads
        realtimeService.only<schemaService.IGetObjectsData>(
          updateEvent,
          (data) => {
            store.dispatch(setters.setListItem(data))
          }
        )

        realtimeService.only<schemaService.IGetObjectsData>(
          deleteEvent,
          (data) => store.dispatch(setters.deleteObjectItem([data.uniqId]))
        )
      } else {
        realtimeService.offOnly(insertEvent)
        realtimeService.offOnly(updateEvent)
        realtimeService.offOnly(deleteEvent)
      }
    },

    getObjectAbly: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { uniqId } = payload

        // The realtime payload has no related values: the object is fetched with the enabled related columns
        const columns: state.IStateData['table']['columns'] = yield select(
          selectors.tableColumns
        )

        try {
          const objectItem: IResponse<schemaService.IGetObjectsData> =
            yield call(schemaService.getObjectsById, {
              schemeName: config.schemeName,
              uniqId,
              relatedFields: getRelatedFieldsParam(columns),
            })

          yield put(setters.setListAddFirst(objectItem.data))
        } catch (e) {}
      },
    },

    getObjectsItem: {
      effect: 'latest',
      pending: function* ({ payload }) {
        const { uniqId, skipForbiddenToast } = payload

        const objectItem: IResponse<schemaService.IGetObjectsData> = yield call(
          schemaService.getObjectsById,
          {
            schemeName: config.schemeName,
            uniqId,
            skipForbiddenToast,
          }
        )

        yield put(setters.setItem(objectItem.data))
      },
      clear: function* () {
        yield put(setters.setItem(undefined))
      },
    },
    postObjectsItem: function* ({ payload }) {
      const { data, fullfiledAction, fullfiledActionContext } = payload
      const item: IResponse<schemaService.IGetObjectsData> = yield call(
        schemaService.postObjects,
        {
          schemeName: config.schemeName,
          data,
        }
      )

      if (fullfiledAction) {
        if (typeof fullfiledAction === 'string') {
          yield put(
            createFullfiledAction(fullfiledAction, {
              data: item.data,
              context: fullfiledActionContext,
            })
          )
        }
        if (typeof fullfiledAction === 'object' && fullfiledAction.type) {
          yield put(fullfiledAction)
        }
      }
      yield put(
        app.actions.tost.pending({
          type: 'success',
          title: 'Object has been successfully created',
          description: '',
        })
      )
    },

    changeListItem: function* ({ payload }) {
      yield put(setters.setListItem(payload))
    },

    putObjectsItem: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { uniqId, data, fullfiledAction, fullfiledActionContext } =
          payload

        const item: IResponse<state.IListItem> = yield call(
          schemaService.putObjects,
          {
            schemeName: config.schemeName,
            uniqId,
            data,
          }
        )

        yield put(setters.setItem(item.data))

        if (fullfiledAction) {
          if (typeof fullfiledAction === 'string') {
            yield put(
              createFullfiledAction(fullfiledAction, {
                data: item.data,
                context: fullfiledActionContext,
              })
            )
          }
          if (typeof fullfiledAction === 'object' && fullfiledAction.type) {
            yield put(fullfiledAction)
          }
        }
        yield put(
          app.actions.tost.pending({
            type: 'success',
            title: 'Object has been successfully updated',
            description: '',
          })
        )
      },
    },

    getFieldsGroups: function* ({ payload }) {
      const { limit, cursor } = payload

      const fieldsGroups: IResponse<schemaService.IFieldsGroupData[]> =
        yield call(schemaService.getFieldsGroups, {
          schemeName: config.schemeName,
          limit,
          cursor,
        })

      yield put(
        setters.setFieldsGroups({
          items: fieldsGroups.data,
          previousCursor: fieldsGroups.previousCursor,
          nextCursor: fieldsGroups.nextCursor,
        })
      )

      yield put(actions.syncTableColumns.pending())
    },

    getSchemeRelations: {
      effect: 'latest',
      saga: function* () {
        let relations: schemaService.ISchemeRelations | null = null

        try {
          const response: IResponse<schemaService.ISchemeRelations> =
            yield call(schemaService.getSchemeRelations, {
              schemeName: config.schemeName,
            })

          relations = response.data
        } catch (error) {
          // 404: the backend has no /relations yet. Any failure only means no related columns, no toast
          relations = null
        }

        // Distinct related schemes the columns are built from: the parent and the
        // targets of the visible relationship fields (the own scheme reuses its fields)
        const targets = relations
          ? Array.from(
              new Set([
                ...(relations.parent ? [relations.parent.identifier] : []),
                ...relations.outgoing
                  .filter((field) => !field.isHidden)
                  .map((field) => field.objectScheme.identifier),
              ])
            ).filter((target) => target !== config.schemeName)
          : []

        const loadFields = function* (schemeName: string) {
          try {
            const response: IResponse<schemaService.IFieldsGroupData[]> =
              yield call(schemaService.getFieldsGroups, {
                schemeName,
                limit: 100,
                skipForbiddenToast: true,
              })

            return response.data.flatMap((group) => group.schemas || [])
          } catch (error) {
            // Lost permission / failure: the scheme simply offers no columns
            return [] as schemaService.ISchema[]
          }
        }

        const fieldsPerTarget: schemaService.ISchema[][] = yield all(
          targets.map((target) => call(loadFields, target))
        )

        const bySchemeName = targets.reduce(
          (result, target, index) => ({
            ...result,
            [target]: fieldsPerTarget[index],
          }),
          {} as Record<string, schemaService.ISchema[]>
        )

        // The relations are stored last: once `loaded` is true the columns are
        // rebuilt with the related ones right away (sanitizeSorting relies on it)
        yield put(setters.setRelatedFields(bySchemeName))
        yield put(setters.setRelations(relations))
        yield put(actions.syncTableColumns.pending())
      },
    },

    setTableColumnsPriority: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { identifier, index } = payload

        yield put(
          setters.setTableColumnsPriority({
            identifier,
            index,
          })
        )
        yield put(actions.saveTableColumns.pending())
      },
    },

    setTableColumnsEnable: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { identifier, enable } = payload

        yield put(
          setters.setTableColumnsEnable({
            identifier,
            enable,
          })
        )
        yield put(actions.saveTableColumns.pending())
      },
    },
    setTableColumnsWidth: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { identifier, width } = payload

        yield put(
          setters.setTableColumnsWidth({
            identifier,
            width,
          })
        )
        yield put(actions.saveTableColumns.pending())
      },
    },

    setBoardStagePriority: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardStagePriority(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardStageEnable: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardStageEnable(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardHideFinal: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardHideFinal(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardFieldsPriority: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardFieldsPriority(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardFieldsEnable: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardFieldsEnable(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardCardUpdateFulfiled: function* ({ payload }) {
      yield put(setters.setListItem(payload.data))
    },
    setBoardCardCreateFulfiled: function* ({ payload }) {
      const objectWithPerms: IResponse<schemaService.IGetObjectsData> =
        yield call(schemaService.getObjectsById, {
          schemeName: config.schemeName,
          uniqId: payload.data.uniqId,
        })
      yield put(setters.setListAddFirstBoard(objectWithPerms.data))
    },
    appendCreatedBoardCard: function* ({ payload }) {
      const boardIdentifier = payload.context?.boardIdentifier
      const objectWithPerms: IResponse<schemaService.IGetObjectsData> =
        yield call(schemaService.getObjectsById, {
          schemeName: config.schemeName,
          uniqId: payload.data.uniqId,
        })

      if (!boardIdentifier) {
        return
      }

      const column:
        | { columnStore: IColumnStoreSlice; isHidden: boolean }
        | undefined = yield call(
        resolveBoardColumn,
        boardIdentifier,
        objectWithPerms.data
      )

      if (!column || column.isHidden) {
        return
      }

      yield put(
        column.columnStore.slice.actions.setListAddFirstBoard(
          objectWithPerms.data
        )
      )
    },
    syncUpdatedBoardCard: function* ({ payload }) {
      const boardIdentifier = payload.context?.boardIdentifier
      const sourcePrefix = payload.context?.sourcePrefix
      const objectWithPerms: IResponse<schemaService.IGetObjectsData> =
        yield call(schemaService.getObjectsById, {
          schemeName: config.schemeName,
          uniqId: payload.data.uniqId,
        })

      if (!boardIdentifier) {
        return
      }

      const column:
        | {
            columnPrefix: string
            columnStore: IColumnStoreSlice
            isHidden: boolean
          }
        | undefined = yield call(
        resolveBoardColumn,
        boardIdentifier,
        objectWithPerms.data
      )

      if (!column) {
        return
      }

      if (sourcePrefix && sourcePrefix !== column.columnPrefix) {
        const sourceStore: IColumnStoreSlice = yield call(
          loadColumnStore,
          sourcePrefix
        )

        if (!column.isHidden) {
          yield put(
            column.columnStore.slice.actions.setListAddFirstBoard(
              objectWithPerms.data
            )
          )
        }
        yield put(
          sourceStore.slice.actions.deleteObjectItem([
            objectWithPerms.data.uniqId,
          ])
        )

        return
      }

      yield put(
        column.columnStore.slice.actions.setListItem(objectWithPerms.data)
      )
    },

    postStageSortStart: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortStart, payload)
      },
    },
    postStageSortEnd: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortEnd, payload)
      },
    },
    postStageSortBefore: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortBefore, payload)
      },
    },
    postStageSortAfter: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortAfter, payload)
      },
    },

    syncTableColumns: {
      effect: 'latest',
      saga: function* () {
        const smartListItem: state.IStateData['smartList']['item'] =
          yield select(selectors.smartListItem)

        const smartListItemDefaultUserOptions: state.IStateData['smartList']['itemDefaultUserOptions'] =
          yield select(selectors.smartListItemDefaultUserOptions)

        const userOptions =
          smartListItem?.userOptions || smartListItemDefaultUserOptions

        if (userOptions) {
          const fieldsGroups: state.IStateData['fieldsGroups']['list'] =
            yield select(selectors.fieldsGroupsList)

          const schemes: AppStoreState['schemas']['items'] = yield select(
            appStore.selectors.selectors.schemas
          )

          const relations: state.IStateData['relations'] = yield select(
            selectors.relations
          )
          const relatedFieldsBySchemeName: state.IStateData['relatedFields']['bySchemeName'] =
            yield select(selectors.relatedFieldsBySchemeName)

          const { columns: fieldsGroupsColumns, unresolved } =
            storageObjectsHelpers.setupColumns({
              schemaName: config.schemeName,
              fieldsGroups: fieldsGroups.items,
              schemas: schemes,
              columns: userOptions.columns,
              // `undefined` until GET /relations answered (or came from the cache): related columns stay unresolved
              relations: relations.item,
              relatedFieldsBySchemeName,
            })

          const fieldsGroupsBoard = storageObjectsHelpers.setupBoard({
            schemaName: config.schemeName,
            fieldsGroups: fieldsGroups.items,
            schemas: schemes,
            boardConfig: userOptions?.board,
          })

          yield put(setters.setBoardConfig(fieldsGroupsBoard))
          yield put(setters.setTableColumns(fieldsGroupsColumns))
          yield put(setters.setTableColumnsUnresolved(unresolved))

          yield put(actions.cacheStorage.pending())
        }
      },
    },
    saveTableColumns: {
      effect: 'latest',
      saga: function* () {
        const smartListItem: state.IStateData['smartList']['item'] =
          yield select(selectors.smartListItem)

        const columns: state.IStateData['table']['columns'] = yield select(
          selectors.tableColumns
        )
        const unresolvedColumns: state.IStateData['table']['unresolvedColumns'] =
          yield select(selectors.tableColumnsUnresolved)
        const board: state.IStateData['board']['config'] = yield select(
          selectors.boardConfig
        )

        // Related columns are stored only while enabled: the disabled candidates are
        // rebuilt from the relations on every sync and would only bloat the options
        const entries = columns
          .filter((column) => column.enable || !isRelatedColumn(column))
          .map((column) => ({
            identifier: column.identifier,
            enable: column.enable,
            width: column.width,
            index: column.priority.index,
          }))
        const knownIdentifiers = new Set(
          entries.map((entry) => entry.identifier)
        )

        const userOptions = {
          // Unresolved related entries are written back untouched, resolved ones win
          columns: [
            ...entries,
            ...unresolvedColumns.filter(
              (entry) => !knownIdentifiers.has(entry.identifier)
            ),
          ],

          board,
        }

        // The options are always stored as the default ones of the scheme. The former
        // per-list branch called patchSmartListUserOptions, which POSTs to a PATCH-only
        // route (405), and per-list options were never read back (see setSmartListItem),
        // so the table has always shown the default options. Per-list configs are a follow-up.
        yield call(smartListService.patchSmartListDefaultUserOptions, {
          schemeName: config.schemeName,
          userOptions: userOptions,
        })
        yield put(setters.setSmartListItemDefaultUserOptions(userOptions))

        if (smartListItem) {
          // syncTableColumns reads the options of the selected list first: keep its
          // in-memory copy in step with what has just been stored
          yield put(
            setters.setSmartListUpdateUserOptions({
              id: smartListItem.id,
              userOptions,
            })
          )
        }

        yield put(actions.syncTableColumns.pending())
      },
    },
    getSmartList: function* ({ payload }) {
      const smartList: IResponse<smartListService.IGetSmartListData> =
        yield call(smartListService.getSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })

      if (payload.cursor) {
        yield put(
          setters.setSmartListsAppend({
            items: smartList.data,
            nextCursor: smartList.meta.nextCursor,
          })
        )
      } else {
        yield put(
          setters.setSmartLists({
            items: smartList.data,
            nextCursor: smartList.meta.nextCursor,
          })
        )
      }
    },

    getSmartListItem: {
      pending: function* ({ payload }) {
        const smartListItem: IResponse<smartListService.IGetSmartListItemData> =
          yield call(smartListService.getSmartListItem, {
            schemeName: config.schemeName,
            ...payload,
          })

        yield put(setters.setSmartListItem(smartListItem.data))
        yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItem(undefined))
        yield put(actions.syncTableColumns.pending())
      },
    },
    getSmartListItemDefault: {
      pending: function* ({ payload }) {
        try {
          const smartListItem: IResponse<smartListService.IGetSmartListItemData> =
            yield call(smartListService.getSmartListItemDefault, {
              schemeName: config.schemeName,
              ...payload,
            })

          yield put(setters.setSmartListItemDefault(smartListItem.data))
        } catch (error) {
          const smartListItem: IResponse<smartListService.IGetSmartListItemData> =
            yield call(smartListService.postSmartListDefault, {
              schemeName: config.schemeName,
              data: {},
            })

          yield put(setters.setSmartListItemDefault(smartListItem.data))
        }
        yield put(actions.getSmartListDefaultUserOptions.pending({}))
        yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItemDefault(undefined))
        yield put(actions.syncTableColumns.pending())
      },
    },
    setSmartListItem: {
      pending: function* ({ payload }) {
        yield put(setters.setSmartListItem(payload))
        // yield put(
        //   actions.getSmartListUserOptions.pending({
        //     uniqId: payload.id,
        //   })
        // )
        // yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItem(undefined))
        // yield put(actions.syncTableColumns.pending())
      },
    },
    setSmartListItemDefault: {
      pending: function* ({ payload }) {
        yield put(setters.setSmartListItemDefault(payload))
        // yield put(actions.getSmartListDefaultUserOptions.pending({}))
        // yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItemDefault(undefined))
        // yield put(actions.syncTableColumns.pending())
      },
    },
    putSmartList: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPutSmartListData> =
        yield call(smartListService.putSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListUpdate(smartList.data))

      yield put(
        appStore.actions.navigate({
          search: {
            smartListSelectedId: smartList.data.id,
          },
        })
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },

    postSmartList: function* ({ payload }) {
      console.debug({ 'MANUAL LIST': payload })

      if (payload.data.type === 'manual') {
        yield put(actions.postManualList.pending(payload))
      } else {
        const smartList: IResponse<smartListService.IPutSmartListData> =
          yield call(smartListService.postSmartList, {
            schemeName: config.schemeName,
            ...payload,
          })

        yield put(setters.setSmartListItem(smartList.data))
        yield put(setters.setSmartListAdd(smartList.data))

        // Subscribe to recalculation events immediately after creation
        subscribeToSmartListRecalculation()

        yield put(
          appStore.actions.navigate({
            search: {
              smartListSelectedId: smartList.data.id,
            },
          })
        )
      }
      yield put(
        app.actions.smartListsCountUp.pending({
          schemeName: config.schemeName,
          number: 1,
        })
      )
    },
    postManualList: function* ({ payload }) {
      console.debug('postManualList', payload)
      const smartList: IResponse<smartListService.IPutSmartListData> =
        yield call(smartListService.postSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })

      yield put(setters.setSmartListItem(smartList.data))
      yield put(setters.setSmartListAdd(smartList.data))

      // Subscribe to recalculation events for manual list too
      subscribeToSmartListRecalculation()

      if (payload.data.objectIds?.length) {
        yield call(smartListService.postManualListObjectsAssign, {
          schemeName: config.schemeName,
          uniqId: smartList.data.id,
          objectsUniqIds: payload.data.objectIds,
        })
      }
      yield put(
        appStore.actions.navigate({
          search: {
            smartListSelectedId: smartList.data.id,
          },
        })
      )
    },

    deleteSmartList: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPutSmartListData> =
        yield call(smartListService.deleteSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListItem(undefined))
      yield put(
        appStore.actions.navigate({
          search: {
            smartListSelectedId: undefined,
          },
        })
      )

      yield put(setters.setSmartListRemove(payload))
      yield put(
        app.actions.smartListsCountUp.pending({
          schemeName: config.schemeName,
          number: -1,
        })
      )
    },

    postManualListObjectsAssign: function* ({ payload }) {
      yield call(smartListService.postManualListObjectsAssign, {
        schemeName: config.schemeName,
        ...payload,
      })
    },
    postManualListObjectsRemove: function* ({ payload }) {
      yield call(smartListService.postManualListObjectsRemove, {
        schemeName: config.schemeName,
        ...payload,
      })
      yield put(
        setters.setObjectsUniqIdsRemove({
          objectsUniqIds: payload.objectsUniqIds,
        })
      )
    },

    addSmartListUsers: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.addSmartListUsers,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    removeSmartListUsers: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.removeSmartListUsers,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    addSmartListGroups: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.addSmartListGroups,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    removeSmartListGroups: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.removeSmartListGroups,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },

    getSmartListDefaultUserOptions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IGetSmartListDefaultUserOptionsData> =
        yield call(smartListService.getSmartListDefaultUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })

      yield put(setters.setSmartListItemDefaultUserOptions(smartList.data))
      yield put(actions.syncTableColumns.pending())
    },
    patchSmartListDefaultUserOptions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPatchSmartListDefaultUserOptionsData> =
        yield call(smartListService.patchSmartListDefaultUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListItemDefaultUserOptions(payload.userOptions))
      yield put(actions.syncTableColumns.pending())
    },
    getSmartListUserOptions: function* ({ payload }) {
      const smartListUserOptions: IResponse<smartListService.IGetSmartListUserOptionsData> =
        yield call(smartListService.getSmartListUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })

      yield put(
        setters.setSmartListUpdateUserOptions({
          id: payload.uniqId,
          userOptions: smartListUserOptions.data,
        })
      )
      yield put(actions.syncTableColumns.pending())
    },
    patchSmartListUserOptions: function* ({ payload }) {
      const smartListUserOptions: IResponse<smartListService.IPatchSmartListUserOptionsData> =
        yield call(smartListService.patchSmartListUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(
        setters.setSmartListUpdateUserOptions({
          id: payload.uniqId,
          userOptions: payload.userOptions,
        })
      )
      yield put(actions.syncTableColumns.pending())
    },

    getSmartListPermissions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IGetSmartListPermissionsData> =
        yield call(smartListService.getSmartListPermissions, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListPermissions(smartList.data))
    },

    postSmartListPermissions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPostSmartListPermissionsData> =
        yield call(smartListService.postSmartListPermissions, {
          schemeName: config.schemeName,
          ...payload,
        })
      // yield put(setters.setSmartListPermissions(smartList.data))
    },
    postSmartListPermissionsGroupsAdd: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsGroupsAdd,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    postSmartListPermissionsGroupsRemove: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsGroupsRemove,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    postSmartListPermissionsUsersAdd: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsUsersAdd,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    postSmartListPermissionsUsersRemove: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsUsersRemove,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    importSettingsUpdate: function* ({ payload }) {
      yield put(setters.setImportSettingsUpdate(payload))
      // yield put(actions.cacheStorage.pending())
    },
    logging: {
      effect: (pattern, saga) => takeLatest('*', saga),
      controlled: true,
      saga: function* (data) {
        if (
          data.type.includes(config.prefix) &&
          !data.type.includes('cacheStorage')
        ) {
          yield put(actions.cacheStorage.pending())
        }
      },
    },
    cacheStorage: {
      effect: 'latest',
      controlled: true,
      saga: function* (data) {
        if (data.type.includes(config.prefix)) {
          yield delay(1000)
          try {
            // console.debug('cacheStorage')
            const stateData: state.IStateData = yield select(selectors.data)

            localStorage.setItem(
              config.prefix,
              JSON.stringify({
                ...stateData,

                objects: {
                  ...stateData.objects,
                  deleteValidation: null,
                  item: undefined,
                  list: {
                    items: [], //stateData.objects.list.items.slice(0,25)
                    nextCursor: null,
                    previousCursor: null,
                  },
                },

                smartList: {
                  ...stateData.smartList,
                  list: {
                    items: stateData.smartList.list.items.slice(0, 5),
                  },
                },
                table: {
                  ...stateData.table,
                  filters: {},
                },
              } as state.IStateData)
            )
          } catch (e) {
            console.debug(e)
          }
        }
      },
    },
  })
}
